import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const PREVIEW_PROP = '_bh_upsell_preview';

/** @type {Promise<object | null> | null} */
let pendingDiscountSync = null;

/** @type {Promise<object | null> | null} */
let cartFetchPromise = null;

async function fetchCart() {
  if (!cartFetchPromise) {
    cartFetchPromise = fetch('/cart.js')
      .then((response) => (response.ok ? response.json() : null))
      .finally(() => {
        cartFetchPromise = null;
      });
  }

  return cartFetchPromise;
}

/** @param {object} item */
function isPreviewLine(item) {
  return item?.properties?.[PREVIEW_PROP] === '1';
}

async function cleanupPreviewLines() {
  const cart = await fetchCart();
  if (!cart?.items?.length) return;

  const previewKeys = cart.items.filter(isPreviewLine).map((item) => item.key);
  if (!previewKeys.length) return;

  await Promise.all(
    previewKeys.map((key) =>
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      })
    )
  );
}

function getUpsellConfig() {
  const config = Theme.cartUpsell;
  if (!config?.productId) return null;

  return {
    productId: Number(config.productId),
    discountCode: typeof config.discountCode === 'string' ? config.discountCode.trim() : '',
    discountPercent: Number(config.discountPercent) || 30,
  };
}

/** @param {object} cart @param {number} upsellProductId */
function cartQualifiesForUpsellOffer(cart, upsellProductId) {
  const items = (cart.items || []).filter((item) => !isPreviewLine(item));
  return items.some((item) => item.product_id !== upsellProductId);
}

/** @param {object} cart @param {number} upsellProductId */
function shouldApplyUpsellDiscount(cart, upsellProductId) {
  const items = (cart.items || []).filter((item) => !isPreviewLine(item));
  const hasUpsell = items.some((item) => item.product_id === upsellProductId);
  const hasOther = items.some((item) => item.product_id !== upsellProductId);
  return hasUpsell && hasOther;
}

/** @param {object} cart */
function getCartDiscountCodes(cart) {
  /** @type {Set<string>} */
  const codes = new Set();

  for (const app of cart.cart_level_discount_applications || []) {
    if (app.type === 'discount_code' && app.title) codes.add(app.title);
  }

  for (const item of cart.items || []) {
    for (const alloc of item.line_level_discount_allocations || []) {
      const app = alloc.discount_application;
      if (app?.type === 'discount_code' && app.title) codes.add(app.title);
    }
  }

  return [...codes];
}

/** @param {string} left @param {string} right */
function discountCodesMatch(left, right) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** @param {object} cart @param {string} discountCode */
function cartHasUpsellDiscount(cart, discountCode) {
  return getCartDiscountCodes(cart).some((code) => discountCodesMatch(code, discountCode));
}

/** @param {object} cart */
function discountStateNeedsSync(cart) {
  const config = getUpsellConfig();
  if (!config?.discountCode || !cart?.items) return false;

  const shouldApply = shouldApplyUpsellDiscount(cart, config.productId);
  const hasOurCode = cartHasUpsellDiscount(cart, config.discountCode);

  return (shouldApply && !hasOurCode) || (!shouldApply && hasOurCode);
}

function getCartSectionIds() {
  /** @type {Set<string>} */
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  });

  return [...sectionIds];
}

/** @param {string[]} discountCodes */
async function fetchDiscountCartUpdate(discountCodes) {
  const sectionIds = getCartSectionIds();
  if (!sectionIds.length) return null;

  const config = fetchConfig('json', {
    body: JSON.stringify({
      discount: discountCodes.join(','),
      sections: sectionIds.join(','),
    }),
  });

  const response = await fetch(Theme.routes.cart_update_url, config);
  if (!response.ok) return null;

  return response.json();
}

/** @param {object} data */
function morphCartSections(data) {
  if (!data?.sections) return;

  // Keep the open drawer dialog mounted — full section morph remounts header and closes it.
  const drawerOpen = Boolean(document.querySelector('cart-drawer-component dialog[open]'));
  const mode = drawerOpen ? 'hydration' : 'full';

  for (const sectionId of getCartSectionIds()) {
    if (data.sections[sectionId]) {
      morphSection(sectionId, data.sections[sectionId], mode);
    }
  }
}

/** @param {object} cart */
async function applyDiscountSync(cart) {
  const config = getUpsellConfig();
  if (!config?.discountCode) return null;

  const shouldApply = shouldApplyUpsellDiscount(cart, config.productId);
  const existingCodes = getCartDiscountCodes(cart);
  const nextCodes = shouldApply
    ? [...existingCodes, config.discountCode]
    : existingCodes.filter((code) => !discountCodesMatch(code, config.discountCode));

  return fetchDiscountCartUpdate(nextCodes);
}

function setCheckoutLocked(locked) {
  setCheckoutLock('legacy', locked);
}

/** @type {Set<string>} */
const checkoutLockReasons = new Set();

/** @type {ReturnType<typeof setTimeout> | null} */
let checkoutLockSafetyTimer = null;

/**
 * @param {string} reason
 * @param {boolean} locked
 */
function setCheckoutLock(reason, locked) {
  if (locked) checkoutLockReasons.add(reason);
  else checkoutLockReasons.delete(reason);

  const isLocked = checkoutLockReasons.size > 0;
  applyCheckoutLocked(isLocked);

  if (checkoutLockSafetyTimer) {
    window.clearTimeout(checkoutLockSafetyTimer);
    checkoutLockSafetyTimer = null;
  }

  // Never leave checkout stuck disabled if an unlock path is missed.
  if (isLocked) {
    checkoutLockSafetyTimer = window.setTimeout(() => {
      checkoutLockReasons.clear();
      applyCheckoutLocked(false);
      checkoutLockSafetyTimer = null;
    }, 10000);
  }
}

/** @param {boolean} locked */
function applyCheckoutLocked(locked) {
  document
    .querySelectorAll('.cart__checkout-button, #checkout, [name="checkout"].cart__checkout-button')
    .forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.closest('.cart-drawer, .cart-items__wrapper, .cart-page')) {
        button.disabled = locked || button.dataset.emptyCart === 'true';
        button.setAttribute('aria-busy', locked ? 'true' : 'false');
      }
    });

  document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.pointerEvents = locked ? 'none' : '';
      el.style.opacity = locked ? '0.55' : '';
    }
  });
}

function isCheckoutLocked() {
  return checkoutLockReasons.size > 0 || Boolean(pendingDiscountSync) || Boolean(window.__dcodedLlpAtcBusy);
}

/**
 * Ensures upsell discount matches cart contents before checkout or cart morph.
 * Called from component-cart-items before rendering updated cart HTML.
 *
 * @param {object} [cartHint]
 * @returns {Promise<object | null>}
 */
async function ensureDiscountSynced(cartHint) {
  const config = getUpsellConfig();
  if (!config?.discountCode) return null;

  if (pendingDiscountSync) return pendingDiscountSync;

  pendingDiscountSync = (async () => {
    setCheckoutLock('discount', true);

    try {
      let cart = cartHint?.items ? cartHint : await fetchCart();
      if (!cart) return null;

      if (!discountStateNeedsSync(cart)) return null;

      return await applyDiscountSync(cart);
    } finally {
      setCheckoutLock('discount', false);
      pendingDiscountSync = null;
    }
  })();

  return pendingDiscountSync;
}

/** @param {HTMLElement} upsellRoot */
function getUpsellProductId(upsellRoot) {
  return Number(upsellRoot.dataset.upsellProductId || 0);
}

/** @param {HTMLElement} upsellRoot */
function getVariantPrice(upsellRoot) {
  const variantInput = upsellRoot.querySelector('[ref="variantId"]');
  const fromInput = variantInput instanceof HTMLInputElement ? Number(variantInput.dataset.variantPrice) : 0;
  return fromInput || Number(upsellRoot.dataset.variantPrice) || 0;
}

/** @param {HTMLElement} upsellRoot */
function computeUpsellPrice(upsellRoot) {
  const variantPrice = getVariantPrice(upsellRoot);
  if (!variantPrice) return null;

  const compareAt = Number(upsellRoot.dataset.compareAtPrice) || 0;
  const discountPercent = Number(upsellRoot.dataset.discountPercent) || getUpsellConfig()?.discountPercent || 30;
  const finalPrice = Math.round((variantPrice * (100 - discountPercent)) / 100);
  const originalPrice = compareAt > variantPrice ? compareAt : variantPrice;

  return {
    finalPrice,
    originalPrice,
    hasDiscount: discountPercent > 0 && finalPrice < originalPrice,
    discountPercent,
  };
}

/** @param {HTMLElement} upsellRoot */
function getUpsellPriceContainer(upsellRoot) {
  const productPrice = upsellRoot.querySelector('product-price.bh-cart-upsell__price:not(.visually-hidden)');
  if (!(productPrice instanceof HTMLElement)) return null;

  const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
  return priceContainer instanceof HTMLElement ? priceContainer : null;
}

/** @param {HTMLElement} upsellRoot @param {ReturnType<typeof computeUpsellPrice> | null} priceData */
function renderUpsellPrice(upsellRoot, priceData) {
  const priceContainer = getUpsellPriceContainer(upsellRoot);
  const discountBadge = upsellRoot.querySelector('.bh-cart-upsell__badge');
  const moneyFormat = upsellRoot.dataset.moneyFormat || '{{amount}}';

  if (!(priceContainer instanceof HTMLElement)) return;

  if (!priceData?.hasDiscount) {
    const variantPrice = getVariantPrice(upsellRoot);
    const compareAt = Number(upsellRoot.dataset.compareAtPrice) || 0;
    const currency = upsellRoot.dataset.currency || 'GBP';
    const regularPrice = compareAt > variantPrice ? compareAt : variantPrice;
    const regularFormatted = formatMoney(regularPrice, moneyFormat, currency);

    priceContainer.removeAttribute('data-discounted');
    priceContainer.innerHTML = `
      <span class="visually-hidden">${Theme.translations?.price || 'Price'}</span>
      <span class="price">${regularFormatted}</span>
    `;

    discountBadge?.classList.add('hidden');
    return;
  }

  const currency = upsellRoot.dataset.currency || 'GBP';
  const saleFormatted = formatMoney(priceData.finalPrice, moneyFormat, currency);
  const regularFormatted = formatMoney(priceData.originalPrice, moneyFormat, currency);

  priceContainer.setAttribute('data-discounted', 'true');
  priceContainer.innerHTML = `
    <span class="visually-hidden">${Theme.translations?.price_sale || 'Sale price'}</span>
    <span class="price">${saleFormatted}</span>
    <span class="visually-hidden">${Theme.translations?.price_regular || 'Regular price'}</span>
    <s class="compare-at-price">${regularFormatted}</s>
  `;

  if (discountBadge instanceof HTMLElement && priceData.discountPercent > 0) {
    discountBadge.textContent = `${priceData.discountPercent}% OFF`;
    discountBadge.classList.remove('hidden');
    discountBadge.removeAttribute('aria-hidden');
  } else {
    discountBadge?.classList.add('hidden');
  }
}

/** @param {HTMLElement} upsellRoot @param {object} cart */
function updateUpsellPriceFromCart(upsellRoot, cart) {
  if (!document.contains(upsellRoot)) return;

  const upsellProductId = getUpsellProductId(upsellRoot);

  if (cart.items?.some((item) => item.product_id === upsellProductId && !isPreviewLine(item))) {
    return;
  }

  const priceData = computeUpsellPrice(upsellRoot);
  const qualifies = cartQualifiesForUpsellOffer(cart, upsellProductId);

  renderUpsellPrice(upsellRoot, qualifies ? priceData : null);
}

/** @param {object} cart */
function refreshUpsellPrices(cart) {
  if (!document.querySelector('.bh-cart-upsell')) return;

  for (const upsellRoot of document.querySelectorAll('.bh-cart-upsell')) {
    if (upsellRoot instanceof HTMLElement) {
      updateUpsellPriceFromCart(upsellRoot, cart);
    }
  }
}

/** @param {Event} event */
function getCartFromEvent(event) {
  const resource = event.detail?.resource;
  if (resource?.items) return resource;
  return null;
}

/** @param {object} [cartHint] */
function refreshUpsellPricesFromCart(cartHint) {
  if (cartHint?.items) {
    refreshUpsellPrices(cartHint);
    return;
  }

  void fetchCart().then((fetched) => {
    if (fetched) refreshUpsellPrices(fetched);
  });
}

/** @param {Event} event */
function onCartUpdate(event) {
  if (event.detail?.data?.didError) {
    setCheckoutLock('atc', false);
    return;
  }

  // Cart HTML is morphed after this event; refresh on the next frame so we target the new DOM.
  const cart = getCartFromEvent(event);
  const source = event.detail?.data?.source;
  const addedProductId = Number(event.detail?.data?.productId || 0);
  const upsellProductId = Number(getUpsellConfig()?.productId || 0);

  // Only hide on ATC of the upsell product — never fall back to config id on remove/qty changes.
  if (source === 'product-form-component' && addedProductId && addedProductId === upsellProductId) {
    document.querySelectorAll('.bh-cart-upsell').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (Number(root.dataset.upsellProductId) === addedProductId) {
        root.hidden = true;
      }
    });
  }

  requestAnimationFrame(() => {
    // Remorph may re-insert upsell after remove — clear stale hidden from a prior add.
    document.querySelectorAll('.bh-cart-upsell').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      const id = Number(root.dataset.upsellProductId || 0);
      if (!id) return;
      const stillInCart = Boolean(
        cart?.items?.some((item) => item.product_id === id && !isPreviewLine(item))
      );
      if (!stillInCart) root.hidden = false;
    });

    refreshUpsellPricesFromCart(cart);

    // Keep checkout locked until discount/sections finish settling after PDP or upsell add.
    void (async () => {
      try {
        await ensureDiscountSyncedAndRender(cart);
      } finally {
        if (source === 'product-form-component' || source === 'cart-items-component') {
          setCheckoutLock('atc', false);
          setCheckoutLock('cart', false);
        }
      }
    })();
  });
}

/** @type {boolean} */
let checkoutBypass = false;

async function guardCheckoutAction(action) {
  if (checkoutBypass) {
    action();
    return;
  }

  if (pendingDiscountSync) {
    await pendingDiscountSync;
  }

  const cart = await fetchCart();
  if (!cart || !discountStateNeedsSync(cart)) {
    action();
    return;
  }

  await ensureDiscountSyncedAndRender(cart);
  checkoutBypass = true;
  action();
  checkoutBypass = false;
}

function bindCheckoutGuard() {
  document.addEventListener(
    'click',
    (event) => {
      if (checkoutBypass) return;
      if (!(event.target instanceof Element)) return;

      const checkoutTrigger = event.target.closest(
        '#checkout, .cart__checkout-button, .shopify-payment-button__button, .shopify-payment-button, [data-shopify="payment-button"]'
      );

      if (!checkoutTrigger) return;
      if (!checkoutTrigger.closest('.cart-drawer, .cart-page, .cart-items__wrapper')) return;

      // Block checkout while PDP/upsell ATC or discount sync is still settling.
      if (isCheckoutLocked()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!getUpsellConfig()?.discountCode) return;

      event.preventDefault();
      event.stopPropagation();

      void guardCheckoutAction(() => {
        if (checkoutTrigger instanceof HTMLElement) {
          checkoutTrigger.click();
        }
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (checkoutBypass) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

      if (isCheckoutLocked()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!getUpsellConfig()?.discountCode) return;

      event.preventDefault();
      event.stopPropagation();

      void guardCheckoutAction(() => {
        form.requestSubmit();
      });
    },
    true
  );
}

async function ensureDiscountSyncedAndRender(cartHint) {
  const updated = await ensureDiscountSynced(cartHint);
  if (updated?.sections) {
    morphCartSections(updated);
    refreshUpsellPricesFromCart(updated);
  }
  return updated;
}

async function onDrawerOpen() {
  await ensureDiscountSyncedAndRender();
  refreshUpsellPricesFromCart();
}

function bindCartDrawerOpen() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement)) return;

  drawer.addEventListener(DialogOpenEvent.eventName, onDrawerOpen);
}

function init() {
  window.__bhCartUpsell = {
    ensureDiscountSynced,
    ensureDiscountSyncedAndRender,
    refreshUpsellPricesFromCart,
    lockCheckout: (reason = 'atc') => setCheckoutLock(String(reason), true),
    unlockCheckout: (reason = 'atc') => setCheckoutLock(String(reason), false),
    isCheckoutLocked,
  };

  void cleanupPreviewLines();
  bindCheckoutGuard();
  bindCartDrawerOpen();
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  document.addEventListener('dcoded-llp:atc-start', () => setCheckoutLock('atc', true));

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const upsellRoot = event.target.closest('.bh-cart-upsell');
    if (!(upsellRoot instanceof HTMLElement)) return;

    const script = event.detail?.data?.html?.querySelector(
      '.bh-cart-upsell-variant-context script[type="application/json"]'
    );
    if (script?.textContent) {
      try {
        const variant = JSON.parse(script.textContent);
        if (variant.price) upsellRoot.dataset.variantPrice = String(variant.price);
      } catch {
        /* keep existing */
      }
    }

    void fetchCart().then((cart) => {
      if (cart) refreshUpsellPrices(cart);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
